import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "utilities/i18n";
import {
  useFetchDeviceLogsQuery,
  useRequestDeviceLogsMutation
} from "generated/graphql";
import { ExpandableCard } from "collections/Cards";
import { FeatureFlagEnabled, PolyIcon, SubmitButton } from "elements";
import { DISMISS, useNotification } from "collections";
import { format, differenceInMilliseconds } from "date-fns";
import { LogToFlash } from "./LogToFlash";
import { useDispatch, useSelector } from "react-redux";
import { State } from "store";
import { startFetching, stopFetching, updateLogInfo } from "store/deviceLogs";
import { DATE_FORMAT, SECOND_IN_MILLISECONDS } from "Constants";

const LOG_FETCH_ERROR_MARGIN_MILLISECONDS = -30 * SECOND_IN_MILLISECONDS;

export function useFetchLogsLogic(deviceId: string, delay?: number) {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [, requestDeviceLogs] = useRequestDeviceLogsMutation();

  const dispatch = useDispatch();
  const {
    fetching,
    lastUploadRequestTime,
    downloadLink,
    uploadedOn
  } = useSelector((state: State) => state.deviceLogs[deviceId] ?? {});
  const [notifyUser, setNotifyUser] = useState(true);

  const interval = delay ?? 5 * SECOND_IN_MILLISECONDS;
  const { data, executor } = useFetchDeviceLogsQuery({
    variables: {
      deviceId
    },
    pause: !fetching,
    requestPolicy: "network-only",
    pollInterval: fetching ? interval : undefined
  });

  // Fire the query right away when the page loads.
  useEffect(() => {
    if (!fetching) {
      executor({ requestPolicy: "network-only" });
    }
  }, [fetching, executor]);

  const downloadUrl = data?.accessLogUrl.url || downloadLink || "";
  let uploadedOnString = data?.accessLogUrl.uploadedOn || uploadedOn || "";

  // If uploadedOnString is a blank string, this value will be NaN
  let timeDifferenceFromLastUpload = differenceInMilliseconds(
    new Date(uploadedOnString),
    new Date(lastUploadRequestTime)
  );

  useEffect(() => {
    dispatch(
      updateLogInfo({
        deviceId,
        downloadLink: downloadUrl,
        uploadedOn: uploadedOnString
      })
    );
  }, [deviceId, downloadUrl, uploadedOnString, dispatch]);

  if (lastUploadRequestTime && fetching) {
    // if fetch logs was started by user, check if it is finished
    // if not finished, notify user that logs are not ready (but only once)
    // Allow for some margin of error due to user and server clock out of sync (user clock going in front of the server)
    if (timeDifferenceFromLastUpload >= LOG_FETCH_ERROR_MARGIN_MILLISECONDS) {
      addNotification({
        id: "device-logs-fetch",
        type: "success",
        message: t("LOGS_SUCCESS_MESSAGE"),
        dismiss: DISMISS.DEFAULT
      });
      dispatch(
        stopFetching({
          deviceId
        })
      );
    } else if (!!data && notifyUser) {
      setNotifyUser(false);
      addNotification({
        id: "device-logs-fetch",
        type: "info",
        message: t("FETCH_LOGS_WARNING"),
        dismiss: DISMISS.DEFAULT
      });
    }
  }

  async function fetchLogs() {
    setNotifyUser(false);
    await requestDeviceLogs({ deviceId });
    dispatch(
      startFetching({
        deviceId
      })
    );
  }

  function cancelFetchLogs() {
    dispatch(
      stopFetching({
        deviceId
      })
    );
  }

  return {
    fetchLogs,
    cancelFetchLogs,
    uploadedOnString,
    downloadUrl,
    fetching
  };
}

export const Diagnostics: React.FC<{
  deviceId: string;
  connected: boolean;
  isLogToFlashSupported: boolean;
}> = ({ deviceId, connected, isLogToFlashSupported }) => {
  const { t } = useTranslation();

  const {
    uploadedOnString,
    downloadUrl,
    fetchLogs,
    cancelFetchLogs,
    fetching
  } = useFetchLogsLogic(deviceId);

  const isUrlAvailable = !!downloadUrl;

  let fileName = "device_logs.log"; // default value, will be replaced with the real fileName when it comes in.

  if (isUrlAvailable) {
    const urlArray = downloadUrl.split("?")[0].split("/");
    fileName = urlArray[urlArray.length - 1];
  }

  return (
    <div className="diagnostics settings scrollable form grid">
      <div className="card title">
        <span className="title pl-1">{t("RETRIEVE_LOGS")}</span>
      </div>
      {isUrlAvailable && (
        <>
          <div className="form-group">
            <label className="col-3 opacity-50 text-right">
              {t("DOWNLOAD_LOGS")}
            </label>
            <div className="col-5 link-group pb-5">
              <a href={downloadUrl} className="font-sm download-link" download>
                {fileName}
                <PolyIcon
                  icon="download_arrow"
                  className="download_arrow pl-5"
                />
              </a>
            </div>
          </div>
          <div className="form-group">
            <label className="col-3 opacity-50 text-right">
              {t("LOGS_UPLOADED_ON")}
            </label>
            <div className="col-5 pb-5">{`${format(
              new Date(uploadedOnString),
              DATE_FORMAT.WITH_TIME_LONG
            )}`}</div>
          </div>
        </>
      )}

      <div className="form-group mb-3">
        <label className="col-3 opacity-50 text-right">
          {t("GET_LATEST_LOGS")}
        </label>
        <div className="col-9">
          <SubmitButton
            isSubmitting={fetching}
            disabled={!connected}
            labelKey="FETCH_LOGS"
            submittingLabelKey="FETCHING"
            onClick={() => {
              fetchLogs();
            }}
          />
          {fetching && (
            <button
              className="btn btn-secondary"
              onClick={() => cancelFetchLogs()}
            >
              {t("CANCEL")}
            </button>
          )}
          {!connected && (
            <div className="error-message has-error">
              {t("FETCH_LOGS_OFFLINE_TEXT")}
            </div>
          )}
        </div>
      </div>
      {fetching && (
        <div
          className="waiting-text opacity-50 italic mb-2"
          data-testid="fetching-logs"
        >
          <Trans i18nKey="FETCH_LOGS_WAITING">
            <p>
              Fetching the logs from the device. The download link will be
              updated when the logs are uploaded.
            </p>
            This might take a while, you can leave this page and come back later
            if you wish.",
          </Trans>
        </div>
      )}
      <FeatureFlagEnabled feature="POLYOS_LOGGING_TO_FLASH">
        {isLogToFlashSupported && connected && (
          <>
            <ExpandableCard
              collapsedContent={
                <div className="category-title pl-5">{t("CONFIGURE_LOGS")}</div>
              }
              expanded={true}
              dropdown
            >
              <LogToFlash deviceId={deviceId} />
            </ExpandableCard>
          </>
        )}
      </FeatureFlagEnabled>
    </div>
  );
};
